import { useEffect, useState } from 'react';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import RoofingOutlinedIcon from '@mui/icons-material/RoofingOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { Title, useCanAccess, useDataProvider, useGetIdentity, useNotify, useStore } from 'react-admin';
import type { CrmDataProvider } from '@/providers/types';
import { hasWidgetPermission, type WidgetIdentity } from '@/providers/commons/hasWidgetPermission';
import { useNavigate } from 'react-router-dom';
import { metricsPeriodRangeLabel } from '@/utils/date_utils';
import {
    buildFeatureNavItems,
    soleFeatureRedirectTo,
} from '@/components/layout/navAccess';
import { DashboardSkeleton } from './components/DashboardSkeleton';
import { MetricCard } from './components/MetricCard';
import { MetricsLineChart } from './components/MetricsLineChart';
import { MetricsUserBarChart } from './components/MetricsUserBarChart';
import { PeriodToggle } from './components/PeriodToggle';
import { RoofTypeMaterialChart } from './components/RoofTypeMaterialChart';
import { MyCardsWidget } from './components/MyCardsWidget';
import { SeriesToggle } from './components/SeriesToggle';
import { WidgetVisibilityMenu } from './components/WidgetVisibilityMenu';
import { CommissionsWidget } from './components/CommissionsWidget';
import { ActiveProjectsWidget } from './components/ActiveProjectsWidget';
import { QuotationsWidget } from './components/QuotationsWidget';
import { QuotesPendingFinishWidget } from './components/QuotesPendingFinishWidget';
import { OpportunityRangeWidget } from './components/OpportunityRangeWidget';
import {
    COLOR_CONVERTED,
    COLOR_GENERATED,
    COLOR_LEADS,
    COLOR_PENDING,
    COLOR_QUOTING_FLAT,
    COLOR_QUOTING_MIXED,
    COLOR_QUOTING_SLOPED,
    LABEL_COLOR,
    PERIODS,
    softCardSx,
} from './constants';
import type { PeriodKey, SeriesFilter, UserMetrics } from './types';
import { WIDGETS, type DashboardWidgetKey } from './widgets';
import {
    roofTypeTotal,
    toLineData,
    toMaterialChartData,
    toUserBarData,
    trendCopy,
} from './utils';

export { changePct } from './changePct';

export const DashboardPage = () => {
    const { identity, isPending: identityPending } = useGetIdentity();
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const navigate = useNavigate();
    const [metrics, setMetrics] = useState<UserMetrics | null>(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useStore<PeriodKey>('dashboard.period', 'week');
    const [seriesFilter, setSeriesFilter] = useState<SeriesFilter>('both');
    const [storedVisibleWidgets, setVisibleWidgets] = useStore<Partial<Record<DashboardWidgetKey, boolean>>>(
        'dashboard.widgets.visible',
        {}
    );
    const visibleWidgets = Object.fromEntries(
        WIDGETS.map((w) => [w.key, storedVisibleWidgets?.[w.key] ?? true])
    ) as Record<DashboardWidgetKey, boolean>;
    // Cosmetic gate; the backend enforces each widget:* permission.
    const access = Object.fromEntries(
        WIDGETS.map((w) => [w.key, hasWidgetPermission(identity as WidgetIdentity, w.permission)])
    ) as Record<DashboardWidgetKey, boolean>;
    const show = (key: DashboardWidgetKey) => access[key] && visibleWidgets[key];
    const anyMetricsWidget = WIDGETS.some((w) => w.key !== 'myCards' && access[w.key]);

    const { canAccess: canAccessQuotes, isPending: quotesPending } = useCanAccess({
        resource: 'quotes',
        action: 'list',
    });
    const { canAccess: canAccessLeads, isPending: leadsPending } = useCanAccess({
        resource: 'leads',
        action: 'list',
    });
    const { canAccess: canAccessProjects, isPending: projectsPending } =
        useCanAccess({
            resource: 'projects',
            action: 'list',
        });
    const { canAccess: canAccessContractors, isPending: contractorsPending } =
        useCanAccess({
            resource: 'contractor_owners',
            action: 'list',
        });
    const { canAccess: canAccessTickets, isPending: ticketsPending } =
        useCanAccess({
            resource: 'tickets',
            action: 'list',
        });
    const { canAccess: canAccessCards, isPending: cardsPending } = useCanAccess({
        resource: 'cards',
        action: 'list',
    });
    const { canAccess: canAccessReceiptUpload, isPending: receiptUploadPending } =
        useCanAccess({
            resource: 'receipt',
            action: 'read',
        });
    const { canAccess: canViewDailyReceipts, isPending: dailyReceiptsPending } =
        useCanAccess({
            resource: 'receipt',
            action: 'view_daily_receipts',
        });
    const {
        canAccess: canAdminReconciliation,
        isPending: adminReconciliationPending,
    } = useCanAccess({
        resource: 'receipt',
        action: 'admin_reconciliation',
    });

    const accessPending =
        identityPending ||
        quotesPending ||
        leadsPending ||
        projectsPending ||
        contractorsPending ||
        ticketsPending ||
        cardsPending ||
        receiptUploadPending ||
        dailyReceiptsPending ||
        adminReconciliationPending;

    // Home shows the cards widget alone when it is the only permitted widget.
    const showCardsWidget = access.myCards && !!canAccessCards;

    const isTeamView = metrics?.scope === 'team';
    const periodMeta = PERIODS.find(p => p.key === period) ?? PERIODS[0];

    useEffect(() => {
        if (accessPending) return;
        const featureItems = buildFeatureNavItems({
            leads: !!canAccessLeads,
            quotes: !!canAccessQuotes,
            projects: !!canAccessProjects,
            contractors: !!canAccessContractors,
            tickets: !!canAccessTickets,
            cards: !!canAccessCards,
            receiptUpload: !!canAccessReceiptUpload,
            receiptAdmin: !!canViewDailyReceipts || !!canAdminReconciliation,
        });
        const redirectTo = soleFeatureRedirectTo(
            anyMetricsWidget || showCardsWidget,
            featureItems
        );
        if (redirectTo) navigate(redirectTo, { replace: true });
    }, [
        accessPending,
        anyMetricsWidget,
        showCardsWidget,
        canAccessLeads,
        canAccessQuotes,
        canAccessProjects,
        canAccessContractors,
        canAccessTickets,
        canAccessCards,
        canAccessReceiptUpload,
        canViewDailyReceipts,
        canAdminReconciliation,
        navigate,
    ]);

    useEffect(() => {
        if (accessPending) return;
        if (!anyMetricsWidget) {
            setMetrics(null);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        (dataProvider.getMetrics('/me') as Promise<UserMetrics>)
            .then((data: UserMetrics) => {
                if (!cancelled) setMetrics(data);
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    notify(err instanceof Error ? err.message : 'Failed to load metrics', {
                        type: 'error',
                    });
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [accessPending, anyMetricsWidget, notify, dataProvider]);

    const generated = metrics?.generated_quotes?.[period] ?? 0;
    const generatedClients = metrics?.generated_clients?.[period] ?? 0;
    const converted = metrics?.converted_quotes?.[period] ?? 0;
    const leadsConverted = metrics?.converted_leads?.[period] ?? 0;
    const pendingEstimate = metrics?.pending_estimate_sent?.[period] ?? 0;
    const quotingForPeriod = metrics?.quoting_by_roof_type?.[period];
    const quotingSlopedMaterials = quotingForPeriod?.sloped_roof;
    const quotingFlatMaterials = quotingForPeriod?.flat_roof;
    const quotingMixedMaterials = quotingForPeriod?.mixed_roof;
    const quotingSloped = roofTypeTotal(quotingSlopedMaterials);
    const quotingFlat = roofTypeTotal(quotingFlatMaterials);
    const quotingMixed = roofTypeTotal(quotingMixedMaterials);
    const generatedPrev = metrics?.generated_quotes_previous?.[period] ?? 0;
    const convertedPrev = metrics?.converted_quotes_previous?.[period] ?? 0;
    const leadsConvertedPrev = metrics?.converted_leads_previous?.[period] ?? 0;
    const showGlobalTrend = !(period === 'all_time' && !isTeamView);

    const byUser = metrics?.by_user?.[period] ?? [];
    const chartData = metrics?.timeline
        ? toLineData(metrics.timeline[period] ?? [], period, seriesFilter)
        : [];
    const userBarData = toUserBarData(byUser, seriesFilter);

    const generatedTrend = showGlobalTrend
        ? trendCopy(
              generated,
              generatedPrev,
              periodMeta.priorLabel,
              generated === 0 ? 'No quotes yet' : `New vs ${periodMeta.priorLabel}`
          )
        : { text: 'All-time total', direction: 'flat' as const };

    // Trend on the Clients-generated card follows the clients value, not the quotes value.
    const generatedClientsPrev = metrics?.generated_clients_previous?.[period] ?? 0;
    const generatedClientsTrend = showGlobalTrend
        ? trendCopy(
              generatedClients,
              generatedClientsPrev,
              periodMeta.priorLabel,
              generatedClients === 0 ? 'No clients yet' : `New vs ${periodMeta.priorLabel}`
          )
        : { text: 'All-time total', direction: 'flat' as const };

    const convertedTrend = showGlobalTrend
        ? trendCopy(
              converted,
              convertedPrev,
              periodMeta.priorLabel,
              converted === 0 ? 'No conversions yet' : `New vs ${periodMeta.priorLabel}`
          )
        : { text: 'All-time total', direction: 'flat' as const };

    const leadsTrend = showGlobalTrend
        ? trendCopy(
              leadsConverted,
              leadsConvertedPrev,
              periodMeta.priorLabel,
              leadsConverted === 0
                  ? 'No lead conversions yet'
                  : `New vs ${periodMeta.priorLabel}`
          )
        : { text: 'All-time total', direction: 'flat' as const };

    if (accessPending) return null;

    // No permitted home widget: return nothing
    if (!anyMetricsWidget && !showCardsWidget) return null;

    // Cards widget is the only permitted home widget
    if (!anyMetricsWidget) {
        return (
            <Box
                sx={{
                    py: 3,
                    px: { xs: 2, sm: 3 },
                    minHeight: '70vh',
                    mx: { xs: -2, sm: -1 },
                }}
            >
                <Title title="Home" />
                <MyCardsWidget enabled={true} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                py: 3,
                px: { xs: 2, sm: 3 },
                minHeight: '70vh',
                mx: { xs: -2, sm: -1 },
            }}
        >
            <Title title={isTeamView ? 'Team Metrics' : 'My Metrics'} />
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={2}
                sx={{ mb: 3.5 }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontSize: { xs: 20, sm: 24 },
                            fontWeight: 600,
                            color: '#111827',
                            mb: 0.5,
                        }}
                    >
                        {isTeamView ? 'Team Metrics' : 'My Metrics'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: LABEL_COLOR }}>
                        {isTeamView
                            ? 'Generated and Converted Quotes By user'
                            : identity?.fullName
                              ? `Quote activity for ${identity.fullName}`
                              : 'Your generated and converted quotes'}
                    </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <PeriodToggle value={period} onChange={setPeriod} />
                    <WidgetVisibilityMenu
                        visible={visibleWidgets}
                        access={access}
                        onToggle={(key: DashboardWidgetKey, visible: boolean) => {
                            setVisibleWidgets({ ...visibleWidgets, [key]: visible });
                        }}
                    />
                </Stack>
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -2, mb: 3 }}>
                <Chip
                    size="small"
                    variant="outlined"
                    label={`Showing data from ${metricsPeriodRangeLabel(period, isTeamView)}`}
                    sx={{ color: LABEL_COLOR }}
                />
            </Box>

            {show('myCards') && (
                <Box sx={{ mb: 3 }}>
                    <MyCardsWidget enabled={!!canAccessCards} />
                </Box>
            )}

            {loading && <DashboardSkeleton />}

            {!loading && metrics && (
                <Stack spacing={3}>
                    <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap>
                        {show('generatedQuotes') && (
                            <MetricCard
                                label="Clients Generated"
                                value={generatedClients}
                                accent={COLOR_GENERATED}
                                icon={<DescriptionOutlinedIcon fontSize="small" />}
                                trend={generatedClientsTrend}
                                secondary={{
                                    label: 'Quotes Generated',
                                    value: generated,
                                }}
                            />
                        )}
                        {show('convertedQuotes') && (
                            <MetricCard
                                label={isTeamView ? 'Team Converted' : 'Converted Quotes'}
                                value={converted}
                                accent={COLOR_CONVERTED}
                                icon={<TaskAltIcon fontSize="small" />}
                                trend={convertedTrend}
                            />
                        )}
                        {show('leadsConverted') && (
                            <MetricCard
                                label={isTeamView ? 'Team Leads Converted' : 'Leads Converted'}
                                value={leadsConverted}
                                accent={COLOR_LEADS}
                                icon={<PersonOutlineIcon fontSize="small" />}
                                trend={leadsTrend}
                            />
                        )}
                        {show('pendingToSend') && (
                            <MetricCard
                                label={isTeamView ? 'Team Quotes Pending to Send' : 'Quotes Pending to Send'}
                                value={pendingEstimate}
                                accent={COLOR_PENDING}
                                icon={<MarkEmailUnreadOutlinedIcon fontSize="small" />}
                            />
                        )}
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} flexWrap="wrap" useFlexGap>
                        {show('quotingSloped') && (
                        <Card
                            elevation={0}
                            sx={{
                                ...softCardSx,
                                flex: 1,
                                minWidth: 300,
                            }}
                        >
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ mb: 2 }}>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        sx={{ mb: 1 }}
                                    >
                                        <Box>
                                            <Typography variant="body2" sx={{ color: LABEL_COLOR, fontWeight: 500 }}>
                                                Quoting — Sloped
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 32,
                                                    fontWeight: 700,
                                                    color: '#111827',
                                                }}
                                            >
                                                {quotingSloped}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ color: COLOR_QUOTING_SLOPED, opacity: 0.85 }}>
                                            <RoofingOutlinedIcon fontSize="small" />
                                        </Box>
                                    </Stack>
                                </Box>
                                <Box sx={{ fontSize: 12, color: LABEL_COLOR }}>Material breakdown</Box>
                                <RoofTypeMaterialChart data={toMaterialChartData(quotingSlopedMaterials)} />
                            </CardContent>
                        </Card>
                        )}

                        {show('quotingFlat') && (
                        <Card
                            elevation={0}
                            sx={{
                                ...softCardSx,
                                flex: 1,
                                minWidth: 300,
                            }}
                        >
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ mb: 2 }}>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        sx={{ mb: 1 }}
                                    >
                                        <Box>
                                            <Typography variant="body2" sx={{ color: LABEL_COLOR, fontWeight: 500 }}>
                                                Quoting — Flat
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 32,
                                                    fontWeight: 700,
                                                    color: '#111827',
                                                }}
                                            >
                                                {quotingFlat}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ color: COLOR_QUOTING_FLAT, opacity: 0.85 }}>
                                            <ViewAgendaOutlinedIcon fontSize="small" />
                                        </Box>
                                    </Stack>
                                </Box>
                                <Box sx={{ fontSize: 12, color: LABEL_COLOR }}>Material breakdown</Box>
                                <RoofTypeMaterialChart data={toMaterialChartData(quotingFlatMaterials, 'flat_roof')} />
                            </CardContent>
                        </Card>
                        )}

                        {show('quotingMixed') && (
                        <Card
                            elevation={0}
                            sx={{
                                ...softCardSx,
                                flex: 1,
                                minWidth: 300,
                            }}
                        >
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ mb: 2 }}>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        sx={{ mb: 1 }}
                                    >
                                        <Box>
                                            <Typography variant="body2" sx={{ color: LABEL_COLOR, fontWeight: 500 }}>
                                                Quoting — Mixed
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 32,
                                                    fontWeight: 700,
                                                    color: '#111827',
                                                }}
                                            >
                                                {quotingMixed}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ color: COLOR_QUOTING_MIXED, opacity: 0.85 }}>
                                            <LayersOutlinedIcon fontSize="small" />
                                        </Box>
                                    </Stack>
                                </Box>
                                <Box sx={{ fontSize: 12, color: LABEL_COLOR }}>Material breakdown</Box>
                                <RoofTypeMaterialChart data={toMaterialChartData(quotingMixedMaterials)} />
                            </CardContent>
                        </Card>
                        )}
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} flexWrap="wrap" useFlexGap>
                        {show('commissions') && (
                            <CommissionsWidget
                                period={period}
                                enabled={access.commissions}
                            />
                        )}
                        {show('activeProjects') && (
                            <ActiveProjectsWidget
                                period={period}
                                enabled={access.activeProjects}
                            />
                        )}
                        {show('opportunityRange') && (
                            <OpportunityRangeWidget
                                period={period}
                                enabled={access.opportunityRange}
                            />
                        )}
                    </Stack>

                    {show('metricsChart') && isTeamView && (
                        <Card elevation={0} sx={softCardSx}>
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'stretch', sm: 'center' }}
                                    spacing={1.5}
                                    sx={{ mb: 1.5 }}
                                >
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: 18, color: '#111827' }}>
                                            {periodMeta.label} By User
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: LABEL_COLOR }}>
                                            Counts by user
                                        </Typography>
                                    </Box>
                                    <SeriesToggle value={seriesFilter} onChange={setSeriesFilter} />
                                </Stack>
                                <MetricsUserBarChart data={userBarData} seriesFilter={seriesFilter} />
                            </CardContent>
                        </Card>
                    )}

                    {show('metricsChart') && !isTeamView && (
                        <Card elevation={0} sx={softCardSx}>
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'stretch', sm: 'center' }}
                                    spacing={1.5}
                                    sx={{ mb: 1.5 }}
                                >
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: 18, color: '#111827' }}>
                                            {periodMeta.label} by date
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: LABEL_COLOR }}>
                                            {period === 'all_time'
                                                ? 'All months (last 12)'
                                                : period === 'month'
                                                  ? 'All days of this month'
                                                  : 'All days of this week'}
                                        </Typography>
                                    </Box>
                                    <SeriesToggle value={seriesFilter} onChange={setSeriesFilter} />
                                </Stack>
                                <MetricsLineChart data={chartData} seriesFilter={seriesFilter} />
                            </CardContent>
                        </Card>
                    )}

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} flexWrap="wrap" useFlexGap>
                        {show('quotations') && (
                            <QuotationsWidget
                                enabled={access.quotations}
                            />
                        )}
                        {show('quotesPendingFinish') && (
                            <QuotesPendingFinishWidget enabled={access.quotesPendingFinish} />
                        )}
                    </Stack>
                </Stack>
            )}
        </Box>
    );
};

DashboardPage.path = '/';
